import React, { useState } from 'react';
import { COLORS, DARK_THEME } from '../../constants/colors';
import { FLAGS } from '../../constants/flags';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';

const Countries = () => {
  const [activeCountry, setActiveCountry] = useState(null);
  const { isDarkMode } = useTheme();
  const { language } = useLanguage();

  const countries = [
    {
      name: 'Türkiye',
      capital: 'Ankara',
      population: language === 'tr' ? '85 Milyon' : '85 Million',
      flag: FLAGS.turkey,
      color: COLORS.turkey,
      description: language === 'tr'
        ? "Avrupa ile Asya arasında köprü kuran kurucu üye Türkiye, TDT'nin liderliğinde merkezi bir rol üstlenmektedir."
        : 'A founding member bridging Europe and Asia, Türkiye plays a central role in OTS leadership.',
      highlights: language === 'tr'
        ? ['G20 Üyesi', 'NATO Müttefiki', 'Büyük Ekonomi']
        : ['G20 Member', 'NATO Ally', 'Major Economy'],
    },
    {
      name: language === 'tr' ? 'Azerbaycan' : 'Azerbaijan',
      capital: language === 'tr' ? 'Bakü' : 'Baku',
      population: language === 'tr' ? '10 Milyon' : '10 Million',
      flag: FLAGS.azerbaijan,
      color: COLORS.azerbaijan,
      description: language === 'tr'
        ? 'Doğal kaynaklar ve kültür bakımından zengin olan Azerbaycan, kilit bir enerji ortağıdır.'
        : 'Rich in natural resources and culture, Azerbaijan is a key energy partner.',
      highlights: language === 'tr'
        ? ['Enerji Merkezi', 'Hazar Kapısı', 'Teknolojik Büyüme']
        : ['Energy Hub', 'Caspian Gateway', 'Tech Growth'],
    },
    {
      name: language === 'tr' ? 'Kazakistan' : 'Kazakhstan',
      capital: 'Astana',
      population: language === 'tr' ? '19 Milyon' : '19 Million',
      flag: FLAGS.kazakhstan,
      color: COLORS.kazakhstan,
      description: language === 'tr'
        ? "Denize kıyısı olmayan en büyük ülke ve Yeni İpek Yolu'nun hayati koridoru."
        : 'The largest landlocked country and a vital corridor of the New Silk Road.',
      highlights: language === 'tr'
        ? ['En Geniş Topraklar', 'Zengin Kaynaklar', 'Uzay Sanayii']
        : ['Largest Territory', 'Resource Rich', 'Space Industry'],
    },
    {
      name: language === 'tr' ? 'Kırgızistan' : 'Kyrgyzstan',
      capital: language === 'tr' ? 'Bişkek' : 'Bishkek',
      population: language === 'tr' ? '7 Milyon' : '7 Million',
      flag: FLAGS.kyrgyzstan,
      color: COLORS.kyrgyzstan,
      description: language === 'tr'
        ? 'Görkemli dağları ve zengin göçebe mirasıyla tanınmaktadır.'
        : 'Known for its stunning mountains and rich nomadic heritage.',
      highlights: language === 'tr'
        ? ['Dağ Turizmi', 'Göçebe Kültürü', 'Hidroelektrik Enerji']
        : ['Mountain Tourism', 'Nomadic Culture', 'Hydro Power'],
    },
    {
      name: language === 'tr' ? 'Özbekistan' : 'Uzbekistan',
      capital: language === 'tr' ? 'Taşkent' : 'Tashkent',
      population: language === 'tr' ? '35 Milyon' : '35 Million',
      flag: FLAGS.uzbekistan,
      color: COLORS.uzbekistan,
      description: language === 'tr'
        ? 'Semerkant ve Buhara gibi kadim İpek Yolu şehirlerine ev sahipliği yapmaktadır.'
        : 'Home to ancient Silk Road cities like Samarkand and Bukhara.',
      highlights: language === 'tr'
        ? ['Tarihi Şehirler', 'Pamuk Üretimi', 'Büyüyen Ekonomi']
        : ['Historic Cities', 'Cotton Production', 'Growing Economy'],
    },
  ];

  const observers = [
    { name: language === 'tr' ? 'Macaristan' : 'Hungary', flag: FLAGS.hungary },
    { name: language === 'tr' ? 'Türkmenistan' : 'Turkmenistan', flag: FLAGS.turkmenistan },
    { name: language === 'tr' ? 'KKTC' : 'TRNC', flag: FLAGS.trnc },
    { name: language === 'tr' ? 'EİT' : 'ECO', flag: FLAGS.eco },
  ];

  return (
    <section id="countries" style={{
      ...styles.section,
      backgroundColor: isDarkMode ? DARK_THEME.backgroundSecondary : COLORS.light,
    }}>
      <div style={styles.container}>
        <div style={styles.header}>
          <span style={{
            ...styles.badge,
            backgroundColor: isDarkMode ? `${DARK_THEME.secondary}30` : `${COLORS.secondary}20`,
            color: isDarkMode ? DARK_THEME.secondary : COLORS.secondary,
          }}>{language === 'tr' ? 'Üye Devletler' : 'Member States'}</span>
          <h2 style={{...styles.title, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
            {language === 'tr' ? 'Birlik İçindeki Türk Devletleri' : 'United Turkic Nations'}
          </h2>
          <p style={{...styles.subtitle, color: isDarkMode ? DARK_THEME.textSecondary : '#666'}}>
            {language === 'tr'
              ? 'Dil, kültür ve ortak gelecek vizyonuyla birbirine bağlı beş devlet'
              : 'Five nations connected by language, culture, and shared vision for the future'}
          </p>
        </div>

        <div style={styles.grid}>
          {countries.map((country, index) => (
            <div
              key={index}
              style={{
                ...styles.card,
                backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.white,
                boxShadow: isDarkMode ? `0 4px 20px ${DARK_THEME.shadow}` : '0 4px 20px rgba(0,0,0,0.08)',
                borderTop: `4px solid ${country.color}`,
                transform: activeCountry === index ? 'translateY(-10px)' : 'translateY(0)',
              }}
              onMouseEnter={() => setActiveCountry(index)}
              onMouseLeave={() => setActiveCountry(null)}
            >
              <div style={styles.cardHeader}>
                <img src={country.flag} alt={country.name} style={styles.flagImage} />
                <div>
                  <h3 style={{...styles.countryName, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                    {country.name}
                  </h3>
                  <span style={{...styles.capital, color: isDarkMode ? DARK_THEME.textMuted : '#888'}}>
                    {country.capital}
                  </span>
                </div>
              </div>

              <p style={{...styles.population, color: isDarkMode ? DARK_THEME.textSecondary : '#666'}}>
                <strong>{language === 'tr' ? 'Nüfus:' : 'Population:'}</strong> {country.population}
              </p>

              <p style={{...styles.description, color: isDarkMode ? DARK_THEME.textSecondary : '#555'}}>
                {country.description}
              </p>

              <div style={styles.highlights}>
                {country.highlights.map((highlight, idx) => (
                  <span
                    key={idx}
                    style={{
                      ...styles.highlightTag,
                      backgroundColor: `${country.color}20`,
                      color: country.color,
                    }}
                  >
                    {highlight}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{
          ...styles.observers,
          backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.white,
        }}>
          <h3 style={{...styles.observersTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
            {language === 'tr' ? 'Gözlemci Devletler ve Ortaklar' : 'Observer States & Partners'}
          </h3>
          <div style={styles.observerFlags}>
            {observers.map((obs, index) => (
              <div key={index} style={styles.observerItem}>
                <img src={obs.flag} alt={obs.name} style={styles.observerFlagImage} />
                <span style={{...styles.observerName, color: isDarkMode ? DARK_THEME.textSecondary : '#666'}}>
                  {obs.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: {
    padding: '100px 20px',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '60px',
  },
  badge: {
    display: 'inline-block',
    padding: '8px 20px',
    borderRadius: '50px',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '20px',
  },
  title: {
    fontSize: 'clamp(32px, 5vw, 48px)',
    marginBottom: '15px',
    fontWeight: '700',
  },
  subtitle: {
    fontSize: '18px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '25px',
  },
  card: {
    borderRadius: '16px',
    padding: '30px',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    cursor: 'pointer',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '20px',
  },
  flagImage: {
    height: '48px',
    width: 'auto',
    borderRadius: '6px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
  },
  countryName: {
    fontSize: '22px',
    fontWeight: '700',
    marginBottom: '4px',
  },
  capital: {
    fontSize: '14px',
  },
  population: {
    fontSize: '14px',
    marginBottom: '15px',
  },
  description: {
    fontSize: '15px',
    lineHeight: '1.6',
    marginBottom: '20px',
  },
  highlights: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  highlightTag: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500',
  },
  observers: {
    marginTop: '60px',
    textAlign: 'center',
    padding: '40px',
    borderRadius: '16px',
  },
  observersTitle: {
    fontSize: '20px',
    marginBottom: '25px',
    fontWeight: '600',
  },
  observerFlags: {
    display: 'flex',
    justifyContent: 'center',
    gap: '50px',
    flexWrap: 'wrap',
  },
  observerItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
  },
  observerFlagImage: {
    height: '40px',
    width: 'auto',
    borderRadius: '4px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
  },
  observerName: {
    fontSize: '14px',
  },
};

export default Countries;
